import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import TooltipBody, {
  TooltipBodyDesigns,
} from 'src/components/TooltipBody/TooltipBody'
import { useSingleAnimationState } from 'src/hooks'
import {
  OpacityDimAndUp,
  OpacityUpAndDownAndShiftLeftAndRight,
  OpacityUpAndDownAndShiftRightAndLeft,
} from 'src/style/keyframeUtils'
import { css } from 'styled-components'

export enum TextWithTooltipOnClickSize {
  Medium = 'medium',
}

interface TextWithTooltipOnClickProps {
  testId?: string
  className?: string
  onClick?: () => void
  onHover?: () => void
  text: string | React.ReactElement
  tooltipBody?: string | React.ReactElement
  delayTrigger?: boolean
  triggerNow?: boolean
  size?: TextWithTooltipOnClickSize
  tooltipSize?: TextWithTooltipOnClickSize
  underline?: boolean
  // TODO: handle open direction better
  openLeft?: boolean
  openDown?: boolean
  disabled?: boolean
  hover?: boolean
  grayed?: boolean
  isBetaTagHeader?: boolean
  debugAlwaysOpen?: boolean
  minWidth?: string
}

const TextWithTooltipOnClick: React.FC<TextWithTooltipOnClickProps> = (
  props,
): React.ReactElement => {
  const { animationModifiers, triggerAnimation } = useSingleAnimationState()

  const onClick = (): void => {
    if (!props.delayTrigger && !props.disabled) {
      triggerAnimation()
    }
    if (props.onClick !== undefined) {
      props.onClick()
    }
  }

  useEffect((): void => {
    if (props.triggerNow) {
      triggerAnimation()
    }
  }, [props.triggerNow])

  return (
    <div
      className={props.className}
      data-testid={props.testId}
      css={css`
        position: relative;

        ${(!!props.hover || props.debugAlwaysOpen) &&
        css`
          div {
            opacity: 0;
            transition: opacity ${styleConstants.transitionSpeed.slowest} ease;
          }
          &:hover {
            div {
              opacity: 1;
            }
            .tooltip-body {
              pointer-events: all;
            }
          }
        `}
      `}
    >
      <span
        onClick={onClick}
        onKeyPress={onClick}
        onMouseEnter={props.onHover}
        onFocus={props.onHover}
        role="button"
        tabIndex={0}
        css={css`
          ${getFontCss(
            12,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.regular,
          )}
          display: flex;
          align-items: center;
          transition: color ${styleConstants.transitionSpeed.fast} ease-out;
          transition:
            opacity ${styleConstants.transitionSpeed.medium} ease-out,
            left ${styleConstants.transitionSpeed.medium} ease-out,
            right ${styleConstants.transitionSpeed.medium} ease-out;

          ${!props.disabled &&
          css`
            cursor: pointer;
            &:hover {
              color: ${themeConstants.colors.cta_dark};
            }
          `}

          ${props.size === TextWithTooltipOnClickSize.Medium &&
          css`
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )}
          `}

          ${!props.grayed &&
          !props.hover &&
          animationModifiers.isAnimated &&
          css`
            ${OpacityDimAndUp()}
          `}

          ${!!props.grayed &&
          css`
            opacity: 50%;
          `}

          ${!!props.underline &&
          css`
            text-decoration: underline;
          `}
        `}
      >
        {props.text}
      </span>
      {!props.disabled && (
        <TooltipBody
          className={'tooltip-body'} // needed for hover styling of parent component
          design={
            props.hover
              ? TooltipBodyDesigns.TooltipHover
              : TooltipBodyDesigns.TemporaryAnimated
          }
          css={css`
            position: absolute;
            opacity: ${props.debugAlwaysOpen ? 1 : 0};
            top: -3px;
            border-radius: 5px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )}
            word-break: break-word;
            white-space: nowrap;
            pointer-events: none;

            ${props.tooltipSize === TextWithTooltipOnClickSize.Medium &&
            css`
              width: 300px;
            `}

            ${props.className === 'receipt-tag' &&
            css`
              margin-left: 8px;
            `}

            ${!props.openLeft
              ? css`
                  left: 100%;

                  ${!props.grayed &&
                  !props.hover &&
                  animationModifiers.isAnimated &&
                  css`
                    ${OpacityUpAndDownAndShiftRightAndLeft(110)};
                  `}

                  ${!props.openDown &&
                  getBreakpointDown(breakpointSizes.SM)(css`
                    left: 100%;
                    right: auto;

                    ${!props.grayed &&
                    !props.hover &&
                    animationModifiers.isAnimated &&
                    css`
                      ${OpacityUpAndDownAndShiftRightAndLeft(104)};
                    `}
                  `)}

              ${props.className === 'receipt-tag' &&
                  css`
                    ${!props.openDown &&
                    getBreakpointDown(breakpointSizes.XSM)(css`
                      margin-top: 32px;
                      left: -248%;

                      ${!props.grayed &&
                      !props.hover &&
                      animationModifiers.isAnimated &&
                      css`
                        ${OpacityUpAndDownAndShiftRightAndLeft(104)};
                      `}
                    `)}
                  `}

              ${!!props.isBetaTagHeader &&
                  css`
                    ${!props.openDown &&
                    getBreakpointDown(breakpointSizes.MD)(css`
                      margin-top: 32px;
                      left: -370%;
                    `)}
                  `}
                `
              : css`
                  right: 100%;

                  ${!props.grayed &&
                  !props.hover &&
                  animationModifiers.isAnimated &&
                  css`
                    ${OpacityUpAndDownAndShiftLeftAndRight(110)};
                  `}

                  ${!props.openDown &&
                  getBreakpointDown(breakpointSizes.SM)(css`
                    right: 100%;
                    left: auto;

                    ${!props.grayed &&
                    !props.hover &&
                    animationModifiers.isAnimated &&
                    css`
                      ${OpacityUpAndDownAndShiftLeftAndRight(104)};
                    `}
                  `)}
                `}

            ${props.openDown &&
            css`
              right: 0%;
              top: 100%;
              left: 0%;
              padding-top: 4px;
            `}

            min-width: ${props.minWidth ?? 'auto'};
            z-index: 1;
          `}
        >
          {props.tooltipBody}
        </TooltipBody>
      )}
    </div>
  )
}

export default TextWithTooltipOnClick
